import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiGet, prettifyApiError } from "@/shared/api";
import type { SyncInterval } from "./api";
import { type HaltedLane, type LastRun, type Lettura, leggiLane } from "./automatismi";

/** Stato vivo degli automatismi: elenco, ultimo giro, fermi del supervisor.
 *
 *  Tre GET insieme, ogni 30 secondi e solo a pagina visibile. Stesso schema
 *  di ``useSyncDashboard``: ``setTimeout`` a catena (il giro dopo parte dalla
 *  risposta, mai giri sovrapposti) e pausa quando la scheda e' nascosta.
 *  30 s e non 10: l'ultimo giro di una lane cambia ogni qualche minuto, e
 *  ``/api/sync/last-runs`` e' gia' chiamata dalla pillola di stato. */
const POLL_MS = 30_000;

export type Automatismi = {
  intervals: SyncInterval[];
  lastRuns: LastRun[];
  halted: HaltedLane[];
  /** Lettura di ogni lane visibile, per modulo. */
  letture: Map<string, Lettura>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useAutomatismi(): Automatismi {
  const [intervals, setIntervals] = useState<SyncInterval[]>([]);
  const [lastRuns, setLastRuns] = useState<LastRun[]>([]);
  const [halted, setHalted] = useState<HaltedLane[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    try {
      const [i, r, h] = await Promise.all([
        apiGet<SyncInterval[]>("/api/sync/intervals", { signal: controller.signal }),
        apiGet<LastRun[]>("/api/sync/last-runs", { signal: controller.signal }),
        apiGet<HaltedLane[]>("/api/sync/halted-lanes", { signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;
      setIntervals(i);
      setLastRuns(r);
      setHalted(h);
      setError(null);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(prettifyApiError(err));
    } finally {
      if (inFlight.current === controller) {
        inFlight.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
    let timer: number | null = null;
    let attivo = false;
    const avvia = () => {
      if (attivo) return;
      attivo = true;
      const giro = async (): Promise<void> => {
        await refresh();
        if (attivo) timer = window.setTimeout(() => void giro(), POLL_MS);
      };
      timer = window.setTimeout(() => void giro(), POLL_MS);
    };
    const ferma = () => {
      attivo = false;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };
    const visibilita = () => {
      if (document.hidden) {
        ferma();
      } else {
        void refresh();
        avvia();
      }
    };
    if (!document.hidden) avvia();
    document.addEventListener("visibilitychange", visibilita);
    return () => {
      document.removeEventListener("visibilitychange", visibilita);
      ferma();
      inFlight.current?.abort();
    };
  }, [refresh]);

  const letture = useMemo(() => {
    const perModulo = new Map(lastRuns.map((r) => [r.module, r]));
    const out = new Map<string, Lettura>();
    for (const i of intervals) {
      out.set(
        i.module,
        leggiLane(i.module, i.enabled, i.interval_seconds, perModulo.get(i.module), halted),
      );
    }
    return out;
  }, [intervals, lastRuns, halted]);

  return { intervals, lastRuns, halted, letture, loading, error, refresh };
}
